import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const MultiItemSelector = ({ title, availableItems, selectedItems, onItemsChange, placeholder, type = "single" }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentItem, setCurrentItem] = useState('');
  const [currentQuantity, setCurrentQuantity] = useState(1);

  // Color schemes for different types
  const getColorScheme = () => {
    if (title === "Urns") return {
      bg: "bg-purple-50",
      border: "border-purple-200", 
      button: "bg-purple-100 hover:bg-purple-200 text-purple-700",
      accent: "text-purple-600"
    };
    if (title === "Keepsakes") return {
      bg: "bg-blue-50",
      border: "border-blue-200",
      button: "bg-blue-100 hover:bg-blue-200 text-blue-700", 
      accent: "text-blue-600"
    };
    if (title === "Jewelry") return {
      bg: "bg-amber-50",
      border: "border-amber-200",
      button: "bg-amber-100 hover:bg-amber-200 text-amber-700",
      accent: "text-amber-600" 
    };
    return {
      bg: "bg-gray-50",
      border: "border-gray-200",
      button: "bg-gray-100 hover:bg-gray-200 text-gray-700",
      accent: "text-gray-600"
    };
  };

  const colors = getColorScheme();
  const slug = String(title || 'items').toLowerCase().replace(/[^a-z0-9]+/g, '-');

  const addItem = () => {
    if (!currentItem) return;
    
    const newItem = {
      item: currentItem,
      quantity: currentQuantity
    };
    
    // Check if item already exists
    const existingIndex = selectedItems.findIndex(item => item.item === currentItem);
    
    if (existingIndex >= 0) {
      // Update existing item quantity
      const updatedItems = [...selectedItems];
      updatedItems[existingIndex].quantity += currentQuantity;
      onItemsChange(updatedItems);
    } else {
      // Add new item
      onItemsChange([...selectedItems, newItem]);
    }
    
    // Reset form
    setCurrentItem('');
    setCurrentQuantity(1);
    setShowAddModal(false);
  };

  const removeItem = (index) => {
    const updatedItems = selectedItems.filter((_, i) => i !== index);
    onItemsChange(updatedItems);
  };

  const updateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) {
      removeItem(index);
      return;
    }
    
    const updatedItems = [...selectedItems];
    updatedItems[index].quantity = newQuantity;
    onItemsChange(updatedItems);
  };

  // Render options based on type (grouped for urns, flat for others)
  const renderOptions = () => {
    if (type === "grouped" && typeof availableItems === 'object') {
      return Object.entries(availableItems).map(([group, items]) => (
        <optgroup key={group} label={group}>
          {items.map((item, index) => (
            <option key={`${group}-${index}`} value={`${group} · ${item}`}>
              {item}
            </option>
          ))}
        </optgroup>
      ));
    } else if (Array.isArray(availableItems)) {
      return availableItems.map((item, index) => {
        // Handle both string arrays and object arrays with {name, price} structure
        const itemName = typeof item === 'object' && item.name ? item.name : item;
        const itemValue = typeof item === 'object' && item.name ? item.name : item;
        
        return (
          <option key={index} value={itemValue}>
            {itemName}
          </option>
        );
      });
    }
    return null;
  };

  return (
    <div className="mt-4" data-testid={`${slug}-selector`}>
      <label className={`block text-sm font-medium mb-2 ${colors.accent}`}>{title}</label>
      
      {/* Add Item Button */}
      <button
        type="button"
        onClick={() => setShowAddModal(true)}
        data-testid={`${slug}-add-button`}
        className={`w-full p-3 border-2 border-dashed rounded-lg transition-colors ${colors.button} ${colors.border}`}
      >
        + Add {title === 'Jewelry' ? 'jewelry' : title.toLowerCase().slice(0, -1)}
      </button>

      {/* Selected Items Display - NOW BELOW the Add Button */}
      {selectedItems.length > 0 && (
        <div className="mt-3 space-y-2">
          {selectedItems.map((item, index) => (
            <div key={index} data-testid={`${slug}-selected-item-${index}`} className={`flex items-center justify-between p-3 rounded-lg ${colors.bg} ${colors.border} border`}>
              <div className="flex-1">
                <span className={`text-sm font-semibold ${colors.accent}`}>{item.quantity}x</span>
                <span className="text-sm text-gray-700 ml-2">{item.item}</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  onClick={() => updateQuantity(index, item.quantity - 1)}
                  className="w-7 h-7 text-gray-500 hover:text-red-500 flex items-center justify-center rounded border border-gray-300 hover:border-red-300"
                >
                  −
                </button>
                <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                <button
                  type="button"
                  onClick={() => updateQuantity(index, item.quantity + 1)}
                  className="w-7 h-7 text-gray-500 hover:text-green-500 flex items-center justify-center rounded border border-gray-300 hover:border-green-300"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="w-7 h-7 text-red-500 hover:text-red-700 flex items-center justify-center ml-2"
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-4 w-full max-w-sm">
            <h3 className={`text-lg font-medium mb-4 ${colors.accent}`}>Add {title.slice(0, -1)}</h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Select Item</label>
              <select
                value={currentItem}
                data-testid={`${slug}-item-select`}
                onChange={(e) => setCurrentItem(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                <option value="">{placeholder}</option>
                {renderOptions()}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
              <select
                value={currentQuantity}
                data-testid={`${slug}-quantity-select`}
                onChange={(e) => setCurrentQuantity(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              >
                {[1,2,3,4,5].map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>

            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addItem}
                data-testid={`${slug}-confirm-add-button`}
                disabled={!currentItem}
                className={`flex-1 px-4 py-2 rounded-lg text-white disabled:bg-gray-300 ${colors.button.includes('purple') ? 'bg-purple-600 hover:bg-purple-700' : colors.button.includes('blue') ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export default MultiItemSelector;
